/**
 * Subscription Service
 * Handles recurring billing and subscription management
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

/**
 * Create a new subscription for recurring billing
 */
export const createSubscription = async (userId, subscriptionData) => {
  try {
    const subscription = {
      userId,
      clientId: subscriptionData.clientId,
      clientName: subscriptionData.clientName,
      clientEmail: subscriptionData.clientEmail,
      
      // Billing details
      amount: subscriptionData.amount,
      currency: subscriptionData.currency || 'USD',
      description: subscriptionData.description || '',
      
      // Recurrence settings
      frequency: subscriptionData.frequency, // 'weekly', 'monthly', 'quarterly', 'yearly'
      billingDay: subscriptionData.billingDay || 1, // Day of month for monthly/quarterly/yearly
      
      // Email settings
      emailTemplateId: subscriptionData.emailTemplateId || 'default',
      sendEmailOnGeneration: subscriptionData.sendEmailOnGeneration !== false,
      
      // Payment gateway settings
      paymentGateway: subscriptionData.paymentGateway || 'manual', // 'stripe', 'wise', 'paymongo', 'gcash', 'manual'
      paymentConfig: subscriptionData.paymentConfig || {},
      
      // Status and dates
      status: 'active', // 'active', 'paused', 'cancelled'
      startDate: subscriptionData.startDate || new Date(),
      nextBillingDate: subscriptionData.nextBillingDate || calculateNextBillingDate(
        subscriptionData.startDate || new Date(),
        subscriptionData.frequency,
        subscriptionData.billingDay
      ),
      lastBillingDate: null,
      
      // Additional settings
      autoSend: subscriptionData.autoSend !== false,
      includeLineItems: subscriptionData.lineItems || [],
      notes: subscriptionData.notes || '',
      
      // Metadata
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'subscriptions'), subscription);
    return { subscriptionId: docRef.id, ...subscription };
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

/**
 * Calculate next billing date based on frequency
 */
const calculateNextBillingDate = (startDate, frequency, billingDay = 1) => {
  const date = new Date(startDate);
  
  switch (frequency) {
    case 'weekly':
      date.setDate(date.getDate() + 7);
      break;
    case 'monthly':
      date.setMonth(date.getMonth() + 1);
      date.setDate(Math.min(billingDay, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
      break;
    case 'quarterly':
      date.setMonth(date.getMonth() + 3);
      date.setDate(Math.min(billingDay, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
      break;
    case 'yearly':
      date.setFullYear(date.getFullYear() + 1);
      date.setDate(Math.min(billingDay, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
      break;
    default:
      throw new Error('Invalid frequency');
  }
  
  return date;
};

/**
 * Update a subscription
 */
export const updateSubscription = async (subscriptionId, updateData) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    
    const updates = {
      ...updateData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(subscriptionRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
};

/**
 * Pause a subscription
 */
export const pauseSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      status: 'paused',
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error pausing subscription:', error);
    throw error;
  }
};

/**
 * Resume a subscription
 */
export const resumeSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      status: 'active',
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error resuming subscription:', error);
    throw error;
  }
};

/**
 * Cancel a subscription
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Delete a subscription
 */
export const deleteSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await deleteDoc(subscriptionRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting subscription:', error);
    throw error;
  }
};

/**
 * Get a single subscription
 */
export const getSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    const subscriptionDoc = await getDoc(subscriptionRef);

    if (!subscriptionDoc.exists()) {
      throw new Error('Subscription not found');
    }

    return { id: subscriptionDoc.id, ...subscriptionDoc.data() };
  } catch (error) {
    console.error('Error getting subscription:', error);
    throw error;
  }
};

/**
 * Get all subscriptions for a user
 */
export const getSubscriptions = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.clientId) {
      q = query(q, where('clientId', '==', filters.clientId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting subscriptions:', error);
    throw error;
  }
};

/**
 * Subscribe to subscriptions changes
 */
export const subscribeToSubscriptions = (userId, callback, filters = {}) => {
  if (!db) {
    // Return empty array immediately and a no-op unsubscribe
    callback([]);
    return () => {};
  }

  let q = query(
    collection(db, 'subscriptions'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  if (filters.status) {
    q = query(q, where('status', '==', filters.status));
  }

  return onSnapshot(q, (snapshot) => {
    const subscriptions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(subscriptions);
  });
};

/**
 * Generate invoice from subscription manually
 */
export const generateSubscriptionInvoice = async (subscriptionId) => {
  try {
    const generateFn = httpsCallable(functions, 'generateRecurringInvoice');
    const result = await generateFn({ subscriptionId });
    return result.data;
  } catch (error) {
    console.error('Error generating subscription invoice:', error);
    throw error;
  }
};
